<template>
    <ButtonPopover
        buttonClass="btn-base btn-white !pl-4 !pr-3 h-10"
        :active-button-class="activeFilterBtnClass"
        popoverClass="bg-white dark:bg-slate-800 rounded-lg shadow-lg dark:shadow-slate-900/50 w-[412px] z-30 outline outline-1 outline-offset-[-1px] outline-slate-200 dark:outline-slate-700 p-2"
    >
        <template #button-content>
            <span class="text-primary text-sm font-medium">Card</span>
            <Transition name="fade">
                <span
                    v-if="selectedCards.length > 0"
                    class="w-5 h-5 text-white rounded-full inline-flex items-center justify-center bg-slate-800 text-xs font-medium"
                >
                    {{ selectedCards.length }}
                </span>
            </Transition>
            <Icon icon="chevron-down-small" class="w-5 h-5 icons-primary" />
        </template>
        <template #default="{ closePopover }">
            <div class="divide-y divide-slate-200 dark:divide-slate-700">
                <div class="pb-2">
                    <UiInput
                        id="insights-card-search"
                        label="Search cards"
                        placeholder="Search cards..."
                        v-model:inputValue="cardQuery"
                        :options="{
                            hideLabel: true,
                            inputClass: 'text-sm border-none py-0.5',
                            icon: 'search',
                        }"
                    />
                </div>
                <div class="max-h-48 overflow-y-auto">
                    <div
                        v-if="filteredUsersWithCards.length === 0"
                        class="p-2 text-slate-500 dark:text-slate-400 text-sm gap-2"
                    >
                        No cards with benefits found
                    </div>
                    <div
                        v-for="user in filteredUsersWithCards"
                        :key="user.id"
                        class="mt-2"
                    >
                        <div
                            class="p-2 text-sm font-medium text-slate-500 dark:text-slate-400 flex items-center gap-2"
                        >
                            <Icon icon="user" class="w-5 h-5" />
                            {{ user.name }}
                        </div>
                        <button
                            v-for="card in user.cards"
                            :key="`${user.id}-${card.connected_account_id}`"
                            type="button"
                            class="w-full flex items-center gap-2 p-2 text-slate-800 dark:text-slate-100 text-sm rounded transition-colors outline-none font-normal hover:bg-slate-100 dark:hover:bg-slate-700 focus:bg-slate-100 dark:focus:bg-slate-700 focus:ring-2 focus:ring-inset focus:ring-slate-300 dark:focus:ring-slate-600 active:bg-slate-200 dark:active:bg-slate-600 cursor-pointer"
                            :class="{
                                'font-medium bg-slate-50 dark:bg-slate-700/50':
                                    isCardSelected(card),
                            }"
                            :title="`${card.title} (${card.last_four})`"
                            @click="selectCard(card)"
                        >
                            <div class="shrink-0">
                                <input
                                    type="checkbox"
                                    :checked="isCardSelected(card)"
                                    class="form-checkbox"
                                    @click.stop="selectCard(card)"
                                />
                            </div>
                            <div class="shrink-0">
                                <ImageLoader class="block rounded-sm">
                                    <template #image>
                                        <img
                                            v-if="
                                                card.card_image?.path &&
                                                card.card_image?.file_name
                                            "
                                            :src="`${cdnUrl}/${card.card_image.path}/_small/${card.card_image.file_name}`"
                                            class="h-5 w-[31.72px] rounded-sm"
                                            loading="eager"
                                        />
                                    </template>
                                    <template #preloader>
                                        <div
                                            class="h-5 w-[31.72px] rounded-sm bg-slate-100 dark:bg-slate-700"
                                        ></div>
                                    </template>
                                    <template #error>
                                        <Icon
                                            icon="credit-card"
                                            class="w-8 h-5"
                                        />
                                    </template>
                                </ImageLoader>
                            </div>
                            <div class="flex-1 min-w-0 flex items-center gap-1">
                                <span class="truncate">{{ card.title }}</span>
                                <span
                                    class="flex shrink-0"
                                    v-if="card.last_four"
                                >
                                    ({{ card.last_four }})
                                </span>
                            </div>
                        </button>
                    </div>
                    <div v-if="selectedCards.length > 0">
                        <button
                            type="button"
                            class="w-full flex items-center gap-2 p-2 text-slate-800 dark:text-slate-100 text-sm rounded hover:bg-slate-100 dark:hover:bg-slate-700 focus:bg-slate-100 dark:focus:bg-slate-700 focus:ring-2 focus:ring-inset focus:ring-slate-300 dark:focus:ring-slate-600 active:bg-slate-200 dark:active:bg-slate-600 transition-colors cursor-pointer outline-none font-medium"
                            @click="clearSelection(closePopover)"
                        >
                            <span>Reset</span>
                        </button>
                    </div>
                </div>
            </div>
        </template>
    </ButtonPopover>
</template>

<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import ButtonPopover from '@/components/ui/button-popover.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import UiInput from '@/components/ui/inputs/ui-input.vue';

const store = useCardBenefitsStore();
const cardQuery = ref('');

const { insightsSelectedCards: selectedCards, insightsUsersWithCards } =
    storeToRefs(store);

const cdnUrl = computed(() => window.cdnUrl);

const activeFilterBtnClass =
    '!bg-slate-200 hover:!bg-slate-200 dark:!bg-slate-700 dark:hover:!bg-slate-700';

const filteredUsersWithCards = computed(() => {
    const users = insightsUsersWithCards.value || [];
    if (!cardQuery.value) return users;
    const query = cardQuery.value.toLowerCase();
    return users
        .map((user) => ({
            ...user,
            cards: user.cards.filter(
                (card) =>
                    card.title.toLowerCase().includes(query) ||
                    (card.last_four && card.last_four.includes(query))
            ),
        }))
        .filter((user) => user.cards.length > 0);
});

const isCardSelected = (card) => {
    return selectedCards.value.some(
        (selected) =>
            selected.connected_account_id === card.connected_account_id
    );
};

const selectCard = (card) => {
    store.toggleInsightsCardFilter({
        connected_account_id: card.connected_account_id,
        title: card.title,
    });
    cardQuery.value = '';
};

const clearSelection = (closePopover) => {
    store.resetInsightsCardFilter();
    cardQuery.value = '';
    if (closePopover) closePopover();
};
</script>
